import { useMemo } from 'react';
import { Box } from '@mui/material';
import { ValueParserFunc } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import { PendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import { integerValueFormatter } from '../../utils/numberUtils';
import { parseBudgetCell } from './helpers';
import { useRateGrid } from './useRateGrid';

interface ContractABTGridProps {
    billingConfigs: BillingConfigWithRelations[];
    isLoading: boolean;
    /** The page's pending edits — this grid records into them; the page saves them (CALL-779). */
    pending: PendingConfigEdits;
    /** CALL-781: hide month COLUMNS before the current month. Display only. */
    hidePastMonths?: boolean;
}

// The shared budget cell parser (German-pinned, CALL-794), rounded because abtSeconds is a whole number
// of seconds — the backend schema rejects a fraction, so rounding here is what keeps a pasted "540,4"
// from turning a save into a 400.
const parseAbt: ValueParserFunc = (params) => {
    const parsed = parseBudgetCell(params.newValue);
    return parsed === null ? null : Math.round(parsed);
};

const getEnteredAbt = (config: BillingConfigWithRelations): Map<string, number> =>
    new Map((config.contractABTs || []).map((abt) => [String(abt.month), Number(abt.abtSeconds)]));

// CALL-752: anchor the ABT forward-fill horizon on the minute-price map (the cost path gates ABT
// extrapolation on prices), so the ABT the budget actually charges stays visible and overridable.
const getPriceAnchor = (config: BillingConfigWithRelations): Map<string, number> =>
    new Map((config.minutePrices || []).map((mp) => [String(mp.month), Number(mp.price)]));

// CALL-822: a monthly entry silently overrides the Standard-ABT in the cost calculation (the export's
// ABT column keeps showing the Standard-ABT), so a diverging cell is tinted and names the overridden
// value — the mix-up behind CALL-819 was invisible without it.
// A Standard-ABT of 0 is "no reference" (the dialog only accepts a positive one; 0 can still arrive via
// import), unlike a Standard-Minutenpreis, where 0 is a real price — so the grid hook gates on
// null and each grid decides what null means.
const getDefaultAbt = (config: BillingConfigWithRelations): number | null =>
    config.defaultAbt == null || Number(config.defaultAbt) <= 0 ? null : Number(config.defaultAbt);

// CALL-745: contract ABT is global master data; editable for budget users.
const ContractABTGrid = ({ billingConfigs, isLoading, pending, hidePastMonths = false }: ContractABTGridProps) => {
    const { t } = useTranslation('budget');
    const divergence = useMemo(
        () => ({
            getReference: getDefaultAbt,
            getTooltip: (reference: number) => t('prices.overridesDefaultAbt', { value: reference }),
        }),
        [t]
    );
    const { gridRef, rowData, columnDefs, defaultColDef, onCellValueChanged, onCellEditingStopped } = useRateGrid({
        hidePastMonths,
        billingConfigs,
        editKind: 'contractABTEdits',
        pending,
        minWidth: 90,
        valueFormatter: integerValueFormatter,
        valueParser: parseAbt,
        getEnteredMap: getEnteredAbt,
        getAnchorMap: getPriceAnchor,
        divergence,
    });

    // CALL-794: `budget-grid` — the class App.css's inline-editor height rule is scoped to. See
    // MinutePriceGrid for what its absence looked like (a 72px editor over the next row).
    return (
        <Box className="budget-grid">
            <TableGridItem
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={onCellValueChanged}
                onCellEditingStopped={onCellEditingStopped}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                height={'auto'}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('hint.noContractAbt')}</span>}
            />
        </Box>
    );
};

export default ContractABTGrid;
